import { useCallback, useState, useReducer } from 'react';

/*
const searchQuery = {
  options: {
    search: "Test",
    country: ["CA", "US"],
  },
  ordering: {
    name: "ASC",
    email: "DESC"
  },
  pagination: {
    page: 1,
    lastId: 234,
    limit: 20
  }
}
*/

/*
const searchQuery = {
  options: [
    {
      key: "search",
      value: value,
      operation: "EQ",
      predicate: "OR"
    },
    {
      key: "search",
      value: value,
      operation: "SW",
      predicate: "AND"
    }
  ],
  ordering: {
    name: "ASC",
    email: "DESC"
  },
  pagination: {
    page: 1,
    lastId: 234,
    limit: 20
  }
}
*/

// const defaultValuesObj = {
//   options: {},
//   ordering: {},
//   pagination: {
//     page: 1,
//     limit: 20
//   }
// };

const defaultValuesObj = {
  filters: [],
  ordering: {},
  pagination: {
    page: 1,
    limit: 20
  }
};

function mapReducer(state, action) {
  switch (action.type) {
    case 'set':
      state.set(action.key, action.value);
      return new Map(state);
    case 'delete':
      state.delete(action.key);
      return new Map(state);
    case 'clear':
      return new Map();
    default:
      return state;
  }
}

const useSearchHelper = (initialValues = {}) => {
  const [state, setState] = useState({ ...defaultValuesObj, ...initialValues });
  const [map, dispatch] = useReducer(mapReducer, new Map());

  const setCustomOption = useCallback(
    (option, valueCallback) => {
      const currentValue = state[option];
      const result = valueCallback(currentValue);
      setState((prev) => {
        const copy = { ...prev };
        copy[option] = result;
        return copy;
      });
    },
    [state, setState]
  );

  const getCustomOption = useCallback(
    (option) => {
      return state[option];
    },
    [state]
  );

  const setOption = useCallback(
    (option, valueCallback) => {
      const currentValue = state.filters[option];
      const result = valueCallback(currentValue);
      setState((prev) => {
        const optionsCopy = { ...prev.filters };
        optionsCopy[option] = result;
        return {
          ...prev,
          filters: optionsCopy
        };
      });
    },
    [state, setState]
  );

  const setPagination = useCallback(
    (option, result) => {
      setState((prev) => {
        const paginationCopy = { ...prev.pagination };
        paginationCopy[option] = result;
        return {
          ...prev,
          pagination: paginationCopy
        };
      });
    },
    [state, setState]
  );

  const setOrdering = useCallback((ordering) => {
    setState((prev) => ({
      ...prev,
      ordering: ordering && typeof ordering === 'object' ? { ...ordering } : {}
    }));
  }, []);

  const getOption = useCallback(
    (option) => {
      return state.filters[option];
    },
    [state]
  );

  const selectColumnOrder = useCallback(
    (column, direction) => {
      setState((prev) => {
        let orderingCopy = { ...prev.ordering };
        const currentDirection = orderingCopy[column];
        if (currentDirection === direction) {
          delete orderingCopy[column];
        } else {
          orderingCopy = {
            [column]: direction
          };
        }
        return {
          ...prev,
          ordering: orderingCopy
        };
      });
    },
    [state, setState]
  );

  const getColumnOrderValue = useCallback(
    (column) => {
      return state.ordering[column];
    },
    [state]
  );

  const getFilterCount = useCallback(() => {
    const minCreationDate = getCustomOption('minCreationDate') ? 1 : 0;
    const maxCreationDate = getCustomOption('maxCreationDate') ? 1 : 0;
    return Object.values(state.filters).reduce((count, value) => {
      if (Array.isArray(value)) {
        return count + value.length;
      }
      return count + (value ? 1 : 0);
    }, minCreationDate + maxCreationDate);
  }, [state.filters, getCustomOption]);

  const resetSearchOptions = useCallback(() => {
    setState({ ...defaultValuesObj, ...initialValues });
  }, [state, setState]);

  const getSearchOptions = useCallback(
    (overrideValues = {}) => {
      return {
        ...state,
        ...overrideValues
      };
    },
    [state]
  );

  return {
    setCustomOption,
    getCustomOption,
    setOption,
    getOption,
    setPagination,
    setOrdering,
    selectColumnOrder,
    getColumnOrderValue,
    getFilterCount,
    getSearchOptions,
    resetSearchOptions
  };
};

export default useSearchHelper;
